import React, { useState, useCallback, useRef, useEffect } from 'react';
import './TuringMachineCalculator.css';

interface CalculationStep {
  step: number;
  state: string;
  headPos: number;
  cellValue: string;
  action: string;
  tape: string[];
}

interface CalculationResult {
  result: number;
  expression: string;
  tape: string[];
  steps: CalculationStep[];
  success: boolean;
  error?: string;
}

const TuringMachineCalculator: React.FC = () => {
  const [displayExpression, setDisplayExpression] = useState('');
  const [currentInput, setCurrentInput] = useState('0');
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [memory, setMemory] = useState(0);
  const [calculationHistory, setCalculationHistory] = useState<Array<{expr: string, result: number}>>([]);
  const traceContainerRef = useRef<HTMLDivElement>(null);

  const serverUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : '';

  const handleNumber = (num: number) => {
    if (currentInput === '0') {
      setCurrentInput(String(num));
    } else {
      setCurrentInput(currentInput + String(num));
    }
  };

  const handleDecimal = () => {
    if (!currentInput.includes('.')) {
      setCurrentInput(currentInput + '.');
    }
  };

  const handleNegate = () => {
    if (currentInput !== '0') {
      if (currentInput.startsWith('-')) {
        setCurrentInput(currentInput.substring(1));
      } else {
        setCurrentInput('-' + currentInput);
      }
    }
  };

  const handleOperation = (op: string) => {
    if (currentInput === '0' && displayExpression === '') return;
    
    if (currentInput !== '0') {
      setDisplayExpression(displayExpression + currentInput + op);
    } else if (displayExpression.length > 0) {
      setDisplayExpression(displayExpression.slice(0, -1) + op);
    } else {
      setDisplayExpression(currentInput + op);
    }
    setCurrentInput('0');
  };

  const handleBackspace = () => {
    if (currentInput.length > 1) {
      setCurrentInput(currentInput.slice(0, -1));
    } else {
      setCurrentInput('0');
    }
  };

  const handleClear = () => {
    setDisplayExpression('');
    setCurrentInput('0');
    setResult(null);
    setError('');
  };

  const handleClearEntry = () => {
    setCurrentInput('0');
  };

  const calculate = useCallback(async (expr: string) => {
    if (!expr.trim()) return null;
    
    setError('');
    setLoading(true);
    setIsStreaming(true);

    try {
      const response = await fetch(`${serverUrl}/api/calculator/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expression: expr, streamSteps: true }),
      });

      if (!response.ok) throw new Error('Calculation failed');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      const steps: CalculationStep[] = [];
      let finalResult: CalculationResult | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';

        for (const message of messages) {
          const dataLine = message.split('\n').find(l => l.startsWith('data:'));
          if (dataLine) {
            try {
              const data = JSON.parse(dataLine.substring(5));
              if (data.type === 'step') {
                steps.push(data.data);
                setCurrentStep(steps.length);
              } else if (data.type === 'result') {
                finalResult = {
                  result: data.data.result,
                  expression: expr,
                  tape: data.data.tape || [],
                  steps,
                  success: data.data.success
                };
              }
            } catch (e) { /* ignore parse errors */ }
          }
        }
      }

      if (finalResult) {
        setResult(finalResult);
        return finalResult.result;
      }
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Calculation error');
      return null;
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  }, [serverUrl]);

  const handleEquals = async () => {
    if (displayExpression === '' && currentInput === '0') return;
    
    const fullExpr = displayExpression + currentInput;
    const calcResult = await calculate(fullExpr);
    
    if (calcResult !== null) {
      setCalculationHistory(prev => [...prev, { expr: fullExpr, result: calcResult }]);
      setDisplayExpression('');
      setCurrentInput(String(calcResult));
    }
  };

  const handleSquare = async () => {
    const value = parseFloat(currentInput);
    const expr = `${value}^2`;
    const calcResult = await calculate(expr);
    if (calcResult !== null) setCurrentInput(String(calcResult));
  };

  const handleSquareRoot = async () => {
    const value = parseFloat(currentInput);
    const expr = `sqrt(${value})`;
    const calcResult = await calculate(expr);
    if (calcResult !== null) setCurrentInput(String(calcResult));
  };

  const handleCubeRoot = async () => {
    const value = parseFloat(currentInput);
    const expr = `cbrt(${value})`;
    const calcResult = await calculate(expr);
    if (calcResult !== null) setCurrentInput(String(calcResult));
  };

  const handleReciprocal = async () => {
    const value = parseFloat(currentInput);
    if (value === 0) {
      setError('Cannot divide by zero');
      return;
    }
    const expr = `1/${value}`;
    const calcResult = await calculate(expr);
    if (calcResult !== null) setCurrentInput(String(calcResult));
  };

  const handlePercent = () => {
    const value = parseFloat(currentInput);
    setCurrentInput(String(value / 100));
  };

  const handlePower = () => {
    if (currentInput === '0' && displayExpression === '') return;
    setDisplayExpression((displayExpression || currentInput) + '^');
    setCurrentInput('0');
  };

  const handleFactorial = async () => {
    if (currentInput === '0') return;
    const expr = `${currentInput}!`;
    const calcResult = await calculate(expr);
    if (calcResult !== null) setCurrentInput(String(calcResult));
  };

  const handleParenthesis = (paren: string) => {
    if (paren === '(') {
      if (currentInput !== '0') {
        setDisplayExpression(displayExpression + currentInput + paren);
        setCurrentInput('0');
      } else {
        setDisplayExpression(displayExpression + paren);
      }
    } else if (paren === ')') {
      if (currentInput !== '0') {
        setDisplayExpression(displayExpression + currentInput + paren);
        setCurrentInput('0');
      } else if (displayExpression.length > 0) {
        setDisplayExpression(displayExpression + paren);
      }
    }
  };

  const handleMemoryClear = () => setMemory(0);
  const handleMemoryRecall = () => setCurrentInput(String(memory));
  const handleMemoryAdd = () => setMemory(memory + parseFloat(currentInput));
  const handleMemorySubtract = () => setMemory(memory - parseFloat(currentInput));
  const handleMemoryStore = () => setMemory(parseFloat(currentInput));

  useEffect(() => {
    if (traceContainerRef.current && result?.steps?.length) {
      traceContainerRef.current.scrollTop = traceContainerRef.current.scrollHeight;
    }
  }, [result?.steps?.length, currentStep]);

  const renderTape = (tape: string[]) => {
    if (!tape || tape.length === 0) return null;
    const displayTape = tape.slice(0, 30);
    
    return (
      <div className="tape">
        {displayTape.map((cell, idx) => (
          <div 
            key={idx} 
            className={`tape-cell ${cell === '_' ? 'blank' : ''} ${['+','-','*','/'].includes(cell) ? 'operator' : ''}`}
          >
            {cell === '_' ? '∅' : cell}
          </div>
        ))}
        {tape.length > 30 && <span className="tape-more">...+{tape.length - 30}</span>}
      </div>
    );
  };

  return (
    <div className="calculator-page">
      <div className="calculator-container">
        <header className="calculator-header">
          <h1>🧮 Turing Machine Calculator - Advanced</h1>
          <p className="subtitle">Chain unlimited operations - watch the algorithm execute in real-time</p>
        </header>

        <div className="main-grid">
          {/* Calculator Panel */}
          <div className="calculator-panel">
            <h2>Calculator</h2>

            <div className="display">
              <div className="display-expression">{displayExpression || '0'}</div>
              <div className="display-result">{currentInput}</div>
            </div>

            <div className="button-grid-5col">
              {/* Row 1: Memory Functions */}
              <button className="btn memory" onClick={handleMemoryClear}>MC</button>
              <button className="btn memory" onClick={handleMemoryRecall}>MR</button>
              <button className="btn memory" onClick={handleMemoryAdd}>M+</button>
              <button className="btn memory" onClick={handleMemorySubtract}>M−</button>
              <button className="btn memory" onClick={handleMemoryStore}>MS</button>

              {/* Row 2: Advanced Operations */}
              <button className="btn advanced" onClick={handlePercent}>%</button>
              <button className="btn advanced" onClick={handleClearEntry}>CE</button>
              <button className="btn advanced" onClick={handleClear}>C</button>
              <button className="btn advanced" onClick={handleBackspace}>⌫</button>
              <button className="btn operation" onClick={() => handleOperation('/')}>÷</button>

              {/* Row 3: Scientific Operations */}
              <button className="btn advanced" onClick={handleReciprocal}>¹/ₓ</button>
              <button className="btn advanced" onClick={handleSquare}>x²</button>
              <button className="btn advanced" onClick={handleSquareRoot}>√x</button>
              <button className="btn advanced" onClick={handleCubeRoot}>³√x</button>
              <button className="btn operation" onClick={() => handleOperation('*')}>×</button>

              {/* Row 4: Numbers 7-9 */}
              <button className="btn number" onClick={() => handleNumber(7)}>7</button>
              <button className="btn number" onClick={() => handleNumber(8)}>8</button>
              <button className="btn number" onClick={() => handleNumber(9)}>9</button>
              <button className="btn advanced" onClick={handlePower}>xʸ</button>
              <button className="btn operation" onClick={() => handleOperation('-')}>−</button>

              {/* Row 5: Numbers 4-6 */}
              <button className="btn number" onClick={() => handleNumber(4)}>4</button>
              <button className="btn number" onClick={() => handleNumber(5)}>5</button>
              <button className="btn number" onClick={() => handleNumber(6)}>6</button>
              <button className="btn advanced" onClick={handleFactorial}>n!</button>
              <button className="btn operation" onClick={() => handleOperation('+')}>+</button>

              {/* Row 6: Numbers 1-3 */}
              <button className="btn number" onClick={() => handleNumber(1)}>1</button>
              <button className="btn number" onClick={() => handleNumber(2)}>2</button>
              <button className="btn number" onClick={() => handleNumber(3)}>3</button>
              <button className="btn operation" onClick={() => handleParenthesis('(')}>(</button>
              <button className="btn operation" onClick={() => handleParenthesis(')')}>)</button>

              {/* Row 7: 0, Decimal, Negate, Equals */}
              <button className="btn number" onClick={handleNegate}>+/−</button>
              <button className="btn number" onClick={() => handleNumber(0)}>0</button>
              <button className="btn number" onClick={handleDecimal}>.</button>
              <button className="btn equals span-2" onClick={handleEquals} disabled={loading}>=</button>
            </div>

            {error && <div className="error-box">❌ {error}</div>}
          </div>

          {/* Visualization Panel */}
          <div className="visualization-panel">
            <h2>Turing Machine Execution</h2>

            {/* Data Flow Visualization */}
            <div className="data-flow">
              <div className="data-box">
                <h3>Input</h3>
                <div className="data-content">{currentInput}</div>
              </div>
              <div className="arrow">→</div>
              <div className="data-box">
                <h3>Turing Machine</h3>
                <div className="data-content">{loading ? 'Processing...' : (result ? result.expression : 'Ready')}</div>
              </div>
              <div className="arrow">→</div>
              <div className="data-box">
                <h3>Output</h3>
                <div className="data-content">{result?.result ?? '0'}</div>
              </div>
            </div>

            {/* Tape Visualization */}
            <div className="tape-visualization">
              <div className="tape-label">Tape State (Unary Representation)</div>
              {result?.tape ? renderTape(result.tape) : (
                <div className="tape-empty">Enter a calculation to see tape state</div>
              )}
            </div>

            {/* Algorithm Steps */}
            <div className="algorithm-section">
              <div className="algorithm-title">Algorithm Execution Steps</div>
              <div className="algorithm-steps" ref={traceContainerRef}>
                {result?.steps && result.steps.length > 0 ? (
                  result.steps.slice(0, 50).map((step, idx) => (
                    <div key={idx} className="algorithm-step">
                      <span className="step-num">Step {step.step}:</span>
                      <div className="step-text">{step.action}</div>
                      <div className="step-detail">State: {step.state}</div>
                    </div>
                  ))
                ) : (
                  <div className="steps-empty">Enter a calculation to see algorithm steps</div>
                )}
                {result?.steps && result.steps.length > 50 && (
                  <div className="algorithm-step">
                    <span className="step-text">... and {result.steps.length - 50} more steps</span>
                  </div>
                )}
              </div>
            </div>

            {/* Calculation History */}
            <div className="algorithm-section">
              <div className="algorithm-title">Calculation History</div>
              <div className="algorithm-steps">
                {calculationHistory.length > 0 ? (
                  calculationHistory.slice(-10).map((calc, idx) => (
                    <div key={idx} className="algorithm-step">
                      <span className="step-num">Calc {idx + 1}:</span>
                      <div className="step-text">{calc.expr}</div>
                      <div className="step-detail">= {calc.result}</div>
                    </div>
                  ))
                ) : (
                  <div className="steps-empty">No calculations yet</div>
                )}
              </div>
            </div>

            {isStreaming && (
              <div className="streaming-indicator">
                <div className="pulse"></div>
                <span>Streaming step {currentStep}...</span>
              </div>
            )}
          </div>
        </div>

        {loading && !isStreaming && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Computing on Turing Machine...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TuringMachineCalculator;
